import { useState } from "react";
import api from '../api/axios';
import { useNavigate } from "react-router-dom";
import { useTranslation } from "../context/LanguageContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import './EnregistrerBerger.css';
import { FaUser, FaCheck, FaTimes } from "react-icons/fa";

export default function AjouterBerger() {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    prenom: "",
    nom: "",
    telephone: "",
    cni: "",
    adresse: "",
    date_embauche: "",
    actif: true,
  });

  const [fieldErrors, setFieldErrors] = useState({
    prenom: "",
    nom: "",
    cni: "",
    date_embauche: "",
  });
  const [touchedFields, setTouchedFields] = useState({
    prenom: false,
    nom: false,
    cni: false,
    date_embauche: false,
  });
  const [loading, setLoading] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const navigate = useNavigate();

  // Fonction de validation des champs obligatoires
  const validateRequiredField = (name, value) => {
    if (!value || value.trim() === "") {
      return t.fieldRequired;
    }
    return "";
  };

  // Validation d'un champ spécifique
  const validateField = (name, value) => {
    if (name === "prenom" || name === "nom" || name === "cni" || name === "date_embauche") {
      return validateRequiredField(name, value);
    }
    return "";
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
    
    // Validation en temps réel si le champ a été touché
    if (touchedFields[name] || fieldErrors[name]) {
      const fieldError = validateField(name, value);
      setFieldErrors(prev => ({
        ...prev,
        [name]: fieldError
      }));
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    
    // Marquer le champ comme touché
    if (name === "prenom" || name === "nom" || name === "cni" || name === "date_embauche") {
      setTouchedFields(prev => ({
        ...prev,
        [name]: true
      }));
    }
    
    // Valider le champ
    const error = validateField(name, value);
    if (name === "prenom" || name === "nom" || name === "cni" || name === "date_embauche") {
      setFieldErrors(prev => ({
        ...prev,
        [name]: error
      }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Marquer tous les champs obligatoires comme touchés
    const newTouchedFields = {
      prenom: true,
      nom: true,
      cni: true,
      date_embauche: true,
    };
    setTouchedFields(newTouchedFields);
    
    // Valider tous les champs obligatoires
    const newFieldErrors = {
      prenom: validateRequiredField("prenom", formData.prenom),
      nom: validateRequiredField("nom", formData.nom),
      cni: validateRequiredField("cni", formData.cni),
      date_embauche: validateRequiredField("date_embauche", formData.date_embauche),
    };
    setFieldErrors(newFieldErrors);
    
    // Vérifier s'il y a des erreurs
    const hasErrors = newFieldErrors.prenom || newFieldErrors.nom || newFieldErrors.cni || newFieldErrors.date_embauche;
    
    if (hasErrors) {
      return; // Ne pas soumettre si il y a des erreurs
    }
    
    setShowConfirmModal(true);
  };

  const handleConfirmSubmit = async () => {
    setLoading(true);
    
    try {
      await api.post("/api/bergers/", formData);
      setShowConfirmModal(false);
      toast.success(t.shepherdRegisteredSuccess);
      navigate("/enregistrer-berger");
    } catch (err) {
      console.error("Erreur lors de l'enregistrement :", err);
      const errorMessage = err.response?.data?.detail || 
                          err.response?.data?.message ||
                          Object.values(err.response?.data || {}).flat().join(", ") ||
                          t.shepherdRegisterError;
      toast.error(errorMessage);
      setShowConfirmModal(false);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelConfirm = () => {
    setShowConfirmModal(false);
  };

  // Réinitialiser le formulaire
  const resetForm = () => {
    setFormData({
      prenom: "",
      nom: "",
      telephone: "",
      cni: "",
      adresse: "",
      date_embauche: "",
      actif: true,
    });
    setFieldErrors({ prenom: "", nom: "", cni: "", date_embauche: "" });
    setTouchedFields({ prenom: false, nom: false, cni: false, date_embauche: false });
  };

  // Vérifier si tous les champs obligatoires sont remplis et qu'il n'y a pas d'erreurs de validation
  const isFormValid = () => {
    const hasRequiredPrenom = formData.prenom && formData.prenom.trim();
    const hasRequiredNom = formData.nom && formData.nom.trim();
    const hasRequiredCNI = formData.cni && formData.cni.trim();
    const hasRequiredDate = formData.date_embauche && formData.date_embauche.trim();
    const hasNoFieldErrors = Object.values(fieldErrors).every(error => !error);
    
    return hasRequiredPrenom && hasRequiredNom && hasRequiredCNI && hasRequiredDate && hasNoFieldErrors;
  };

  const confirmContent = (
    <div className="confirm-summary">
      <div className="summary-row">
        <span className="summary-label">{t.summaryFirstName}</span>
        <span className="summary-value">{formData.prenom || t.notProvided}</span>
      </div>
      {formData.nom && (
        <div className="summary-row">
          <span className="summary-label">{t.summaryLastName}</span>
          <span className="summary-value">{formData.nom}</span>
        </div>
      )}
      {formData.telephone && (
        <div className="summary-row">
          <span className="summary-label">{t.summaryPhone}</span>
          <span className="summary-value">{formData.telephone}</span>
        </div>
      )}
      {formData.cni && (
        <div className="summary-row">
          <span className="summary-label">{t.summaryCNI}</span>
          <span className="summary-value">{formData.cni}</span>
        </div>
      )}
      {formData.adresse && (
        <div className="summary-row">
          <span className="summary-label">{t.summaryLocation}</span>
          <span className="summary-value">{formData.adresse}</span>
        </div>
      )}
      {formData.date_embauche && (
        <div className="summary-row">
          <span className="summary-label">{t.summaryHireDate}</span>
          <span className="summary-value">{formData.date_embauche}</span>
        </div>
      )}
      <div className="summary-row">
        <span className="summary-label">{t.summaryActive}</span>
        <span className="summary-value">{formData.actif ? t.yes : t.no}</span>
      </div>
    </div>
  );

  return (
    <div className="enregistrer-berger-page">
      <div className="title-badge">
        <FaUser className="page-title-icon" aria-hidden="true" />
        <span>{t.registerShepherd}</span>
      </div>

      <form onSubmit={handleSubmit} className="berger-form">
        {/* Bannière d'information */}
        <div className="form-info-banner">
          <span className="info-icon">ℹ️</span>
          <div className="info-content">
            <strong>{t.requiredFields}</strong>
            <span className="required-indicator">*</span> {t.requiredFieldNote}
          </div>
        </div>

        <div className="form-section-container">
          <div className="form">
            <label htmlFor="prenom">
              {t.firstName} <span className="required-indicator">*</span>
            </label>
            <input 
              type="text" 
              id="prenom" 
              name="prenom" 
              value={formData.prenom} 
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="Ex: Baba"
              className={fieldErrors.prenom ? "input-error" : ""}
              required 
            />
            {fieldErrors.prenom && (
              <span className="field-error-message">{fieldErrors.prenom}</span>
            )}
          </div>

          <div className="form">
            <label htmlFor="nom">
              {t.lastName} <span className="required-indicator">*</span>
            </label>
            <input 
              type="text" 
              id="nom" 
              name="nom" 
              value={formData.nom} 
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="Ex: Diarra"
              className={fieldErrors.nom ? "input-error" : ""}
              required
            />
            {fieldErrors.nom && (
              <span className="field-error-message">{fieldErrors.nom}</span>
            )}
          </div>

          <div className="form">
            <label htmlFor="telephone">{t.phone}</label>
            <input 
              type="tel" 
              id="telephone" 
              name="telephone" 
              value={formData.telephone} 
              onChange={handleChange}
              placeholder="Ex: +223 76 12 34 56"
            />
          </div>

          <div className="form">
            <label htmlFor="cni">
              {t.cni} <span className="required-indicator">*</span>
            </label>
            <input 
              type="text" 
              id="cni" 
              name="cni" 
              value={formData.cni} 
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="Ex: A123456"
              className={fieldErrors.cni ? "input-error" : ""}
              required
            />
            {fieldErrors.cni && (
              <span className="field-error-message">{fieldErrors.cni}</span>
            )}
          </div>

          <div className="form full-width">
            <label htmlFor="adresse">
              {t.location} <span className="field-description">{t.locationHint}</span>
            </label>
            <textarea
              id="adresse"
              name="adresse"
              value={formData.adresse}
              onChange={handleChange}
              placeholder="Ex: Bamako"
              rows="3"
            />
          </div>

          <div className="form">
            <label htmlFor="date_embauche">
              {t.hireDate} <span className="required-indicator">*</span>
            </label>
            <input 
              type="date" 
              id="date_embauche" 
              name="date_embauche" 
              value={formData.date_embauche} 
              onChange={handleChange}
              onBlur={handleBlur}
              className={fieldErrors.date_embauche ? "input-error" : ""}
              required
            />
            {fieldErrors.date_embauche && (
              <span className="field-error-message">{fieldErrors.date_embauche}</span>
            )}
          </div>

          <div className="form">
            <label htmlFor="actif" style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input 
                type="checkbox" 
                id="actif" 
                name="actif" 
                checked={formData.actif || false}
                onChange={handleChange}
                style={{ 
                  width: '18px', 
                  height: '18px', 
                  cursor: 'pointer', 
                  margin: 0,
                  appearance: 'checkbox',
                  WebkitAppearance: 'checkbox',
                  MozAppearance: 'checkbox'
                }}
              />
              <span>{t.active}</span>
            </label>
          </div>
        </div>

        {/* Boutons d'action */}
        <div className="actions">
          <button 
            type="submit" 
            className="save-but"
            disabled={!isFormValid() || loading}
          >
            <FaCheck />
            <span>{loading ? t.registering : t.register}</span>
          </button>
          <button 
            type="button"
            onClick={() => navigate("/enregistrer-berger")} 
            className="cancel-but"
            disabled={loading}
          >
            <FaTimes />
            <span>{t.cancel}</span>
          </button>
        </div>
      </form>

      {/* Modal de confirmation */}
      {showConfirmModal && (
        <div className="confirm-modal-overlay" onClick={handleCancelConfirm}>
          <div className="confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="confirm-modal-header">
              <FaCheck className="confirm-modal-icon" />
              <h3>{t.confirmRegistration}</h3>
            </div>
            <div className="confirm-modal-body">
              <p className="confirm-intro">
                {t.verifyInfoBeforeConfirm}
              </p>
              {confirmContent}
            </div>
            <div className="confirm-modal-footer">
              <button
                type="button"
                onClick={handleCancelConfirm}
                className="confirm-modal-cancel"
                disabled={loading}
              >
                <FaTimes />
                <span>{t.cancel}</span>
              </button>
              <button
                type="button"
                onClick={handleConfirmSubmit}
                className="confirm-modal-confirm"
                disabled={loading}
              >
                <FaCheck />
                <span>{loading ? t.saving : t.confirm}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </div>
  );
}

